<template>
  <div class="subscription-plan">
    <div v-if="!currentPlan" class="subscription-plan__loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>
    <div v-else class="subscription-plan__current">
      <div class="subscription-plan__plan-info">
        <div class="subscription-plan__name-wrapper">
          <h4 class="subscription-plan__plan-name">{{ currentPlan.name }}</h4>
          <span class="subscription-plan__plan-badge">{{ currentPlan.interval === 'year' ? 'Yearly' : 'Monthly' }}</span>
        </div>
        <p class="subscription-plan__plan-description">{{ currentPlan.description }}</p>
        <p v-if="subscriptionPeriod" class="subscription-plan__period">
          <span class="subscription-plan__period-label">Billing period:</span>
          {{ formatDate(subscriptionPeriod.start) }} - {{ formatDate(subscriptionPeriod.end) }}
        </p>
      </div>
      <div class="subscription-plan__features-wrapper">
        <div class="subscription-plan__features">
          <div v-for="(feature, index) in currentPlan.features" :key="index" class="subscription-plan__feature">
            <q-icon name="check_circle" size="20px" color="primary" />
            <span class="subscription-plan__feature-text">{{ feature }}</span>
          </div>
        </div>
      </div>
      <div class="subscription-plan__actions">
        <!-- Use transition to prevent UI flicker -->
        <transition name="fade" mode="out-in">
          <!-- Subscription cancellation notice -->
          <div v-if="isSubscriptionCanceled" key="canceled" class="subscription-plan__cancellation-notice">
            <span>
              Your subscription has been cancelled. Access to your plan will end on
              <span class="subscription-plan__cancellation-date">{{ formatDate(subscriptionPeriod.end) }}</span>
            </span>
          </div>

          <!-- Unsubscribe button (only visible for active paid subscriptions that haven't been canceled) -->
          <q-btn
            v-else-if="currentPlan.is_subscribed && currentPlan.interval !== 'day'"
            key="unsubscribe"
            flat
            no-caps
            class="subscription-plan__unsubscribe-btn"
            @click="$emit('unsubscribe', currentPlan)"
          >
            Unsubscribe
          </q-btn>
        </transition>

        <!-- Upgrade/Hide plans button -->
        <q-btn flat no-caps class="subscription-plan__upgrade-btn" @click="$emit('toggle-plans')">
          <span v-if="showPlans">Hide plans</span>
          <span v-else>Upgrade</span>
        </q-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'SubscriptionPlan',
  props: {
    showPlans: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/getUser',
      currentPlan: 'billing/currentPlan',
      isSubscriptionCanceled: 'billing/isSubscriptionCanceled'
    }),
    subscriptionPeriod() {
      if (this.user && this.user.billing && this.currentPlan.is_subscribed) {
        return {
          start: this.user.billing.period_start,
          end: this.user.billing.period_end
        };
      }
      return null;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },
  // Add activated hook for keep-alive component
  activated() {
    // Force a refresh of computed props when component is reactivated
    this.$forceUpdate();
  }
};
</script>

<style lang="scss" scoped>
.subscription-plan {
  background-color: #fff;
  border-radius: 8px;
  margin-bottom: 24px;

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }

  &__header {
    margin-bottom: 16px;
  }

  &__title {
    @include inter-font($size: 20px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 2px 0;
  }

  &__subtitle {
    @include inter-font($size: 16px, $color: rgba(0, 0, 0, 0.6));
    margin: 0;
  }

  &__current {
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;

    @media (min-width: 768px) {
      flex-direction: row;
      text-align: left;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__plan-info {
    flex: 1;
    width: 100%;

    @media (min-width: 768px) {
      width: auto;
      margin-right: 16px;
    }
  }

  &__name-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }

  &__plan-name {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }

  &__plan-badge {
    @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
    background-color: var(--terziary-btn-color);
    border-radius: 8px;
    padding: 2px 10px;
    margin-left: 8px;
  }

  &__plan-description {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0 0 8px 0;
  }

  &__period {
    @include inter-font($size: 13px, $color: var(--secondary-text-color));
    margin: 0;
    padding: 6px 10px;
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: 6px;
    display: inline-block;

    &-label {
      @include inter-font($size: 13px, $bolded: true, $color: var(--secondary-text-color), $weight: 600);
      margin-right: 4px;
    }
  }

  &__features-wrapper {
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: center;

    @media (min-width: 768px) {
      width: auto;
      justify-content: flex-start;
    }
  }

  &__features {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 400px;
  }

  &__feature {
    display: flex;
    align-items: center;
    gap: 12px;
    @include inter-font($size: 14px, $color: var(--main-text-color));

    .q-icon {
      min-width: 14px;
      flex-shrink: 0;
    }
  }

  &__feature-text {
    flex: 1;
    @include inter-font($size: 14px, $color: var(--main-text-color));
  }

  &__actions {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 12px;

    @media (min-width: 768px) {
      width: auto;
      margin-left: auto;
      justify-content: flex-end;
      align-items: center;
    }
  }

  &__cancellation-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 4px;
    max-width: 200px;
    text-align: left;
    @include inter-font($size: 13px, $color: var(--main-text-color));
  }

  &__cancellation-date {
    @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  }

  &__upgrade-btn {
    background-color: var(--main-btn-color);
    color: var(--main-text-color);
    @include inter-font($size: 14px, $bolded: true);
    border-radius: 8px;
    padding: 8px 24px;
    height: 50px;
    min-width: 120px;
    justify-content: center;
    align-items: center;
  }

  &__unsubscribe-btn {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    border-radius: 8px;
    padding: 8px 24px;
    min-width: 120px;
    height: 50px;
    justify-content: center;
    align-items: center;
    background-color: var(--terziary-btn-color);
  }
}

/* Add transition styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
